import { useCallback, useEffect, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  canManageIntegrations,
  canReplaceCredential,
  checkCredential,
  checkNextcloudSettings,
  integrationProvider,
  integrationStatusText,
  nextcloudHost,
  processorFolder,
  processorFolderProblem,
  type IntegrationRow,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { TextField } from '../components/Fields.tsx';
import {
  loadArchive,
  loadProcessorFolder,
  removeIntegration,
  saveNextcloud,
  setArchiveEnabled,
  setProcessorFolder,
  testNextcloud,
  type ArchiveRow,
  type ConnectionTest,
} from './api.ts';

const when = (iso: string | null): string =>
  iso
    ? new Date(iso).toLocaleString('en-AU', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : 'Never';

/**
 * The file archive (MB decision 376; docs/specs/cloud/file-archive.md §6): Teslyn Cloud storage by default, or the
 * organisation's own Nextcloud. Tenant admins set up and remove Nextcloud (server address, folder, username and an
 * app password); tenant admins and managers replace the login and press "Test connection". Every staff member sees
 * where files go, the last successful upload and the last test. Tenant admins switch archiving off and on (decision
 * 393). The app password is typed once, sent to Vault, and never shown or kept in the page.
 */
export function ArchivePanel({
  sb,
  tenantId,
  roles,
  integration,
  onChanged,
}: {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  readonly roles: readonly string[];
  /** The tenant's Nextcloud integration row, if any (from the Integrations screen's list). */
  readonly integration: IntegrationRow | null;
  /** Reload the Integrations screen's rows (status, last four) after a change. */
  readonly onChanged: () => void;
}): ReactElement {
  const provider = integrationProvider('nextcloud')!;
  const [archive, setArchive] = useState<ArchiveRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [testing, setTesting] = useState(false);
  const [notice, setNotice] = useState<{ readonly ok: boolean; readonly text: string } | null>(null);
  const admin = canManageIntegrations(roles);
  const replacer = canReplaceCredential(roles, provider);

  const reload = useCallback(() => {
    loadArchive(sb, tenantId)
      .then((a) => {
        setArchive(a);
        setError(null);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [sb, tenantId]);
  useEffect(reload, [reload]);

  // Nextcloud is the archive when its integration exists and the archive row says so (they change together)
  const nc =
    integration !== null && archive !== null && archive.destination === 'nextcloud'
      ? { integration, archive }
      : null;

  async function run(action: () => Promise<void>, done: string): Promise<void> {
    try {
      await action();
      setNotice({ ok: true, text: done });
      setEditing(false);
      setConfirmRemove(false);
    } catch (e: unknown) {
      setNotice({ ok: false, text: e instanceof Error ? e.message : String(e) });
    }
    reload();
    onChanged();
  }

  async function test(): Promise<void> {
    setTesting(true);
    setNotice(null);
    let r: ConnectionTest;
    try {
      r = await testNextcloud(sb, tenantId);
    } catch (e: unknown) {
      r = { ok: false, message: e instanceof Error ? e.message : String(e) };
    }
    setTesting(false);
    setNotice({ ok: r.ok, text: r.message });
    reload();
    onChanged();
  }

  return (
    <div className="stack">
      <p className="body">
        Every file Teslyn Field imports or exports is archived by project, year and day. Without Nextcloud the
        archive is in Teslyn Cloud. With Nextcloud, Teslyn Cloud forwards each file to your server with the
        app password stored here, so no laptop ever holds it.
      </p>
      {notice && (
        <p className={notice.ok ? 'notice' : 'notice notice--fail'} role={notice.ok ? 'status' : 'alert'}>
          {notice.text}
        </p>
      )}
      {error && (
        <p className="notice notice--fail" role="alert">
          {error}
        </p>
      )}
      <label className="row body">
        <input
          type="checkbox"
          checked={archive?.enabled ?? true}
          disabled={!admin}
          onChange={(e) => {
            const on = e.target.checked;
            void run(
              () => setArchiveEnabled(sb, tenantId, on),
              on
                ? 'Archiving is switched on: imported and exported files are archived again.'
                : 'Archiving is switched off: imported and exported files are not archived until it is switched on again.',
            );
          }}
        />
        Archive imported and exported files
      </label>
      {archive?.enabled === false && !admin && (
        <p className="caption muted">Archiving is switched off. Only a tenant admin can switch it on.</p>
      )}
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th className="label">Archive</th>
              <th className="label">Status</th>
              <th className="label">Where</th>
              <th className="label">Last successful upload</th>
              <th className="label">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{nc ? 'Nextcloud' : 'Teslyn Cloud'}</td>
              <td>
                {nc ? (
                  <span
                    className={
                      nc.integration.status === 'failing'
                        ? 'chip chip--fail caption'
                        : 'chip chip--pass caption'
                    }
                  >
                    {integrationStatusText(nc.integration)}
                  </span>
                ) : (
                  <span className="chip chip--pass caption">Working</span>
                )}
              </td>
              <td>
                {nc ? (
                  <span className="data">
                    {nextcloudHost(nc.archive.nextcloud_url ?? '')}, {nc.archive.nextcloud_folder}, user{' '}
                    {nc.archive.nextcloud_username} (app password …{nc.integration.last_four})
                  </span>
                ) : (
                  'Teslyn Cloud storage'
                )}
              </td>
              <td>
                {when(archive?.last_success_at ?? null)}
                {archive?.last_success_path && (
                  <div className="caption muted data">{archive.last_success_path}</div>
                )}
              </td>
              <td>
                <div className="row">
                  {nc && replacer && (
                    <Button variant="secondary" busy={testing} onClick={() => void test()}>
                      {testing ? 'Testing…' : 'Test connection'}
                    </Button>
                  )}
                  {nc && replacer && (
                    <Button variant="quiet" onClick={() => setEditing(true)} aria-label="Replace Nextcloud">
                      Replace
                    </Button>
                  )}
                  {nc &&
                    admin &&
                    (confirmRemove ? (
                      <Button
                        variant="secondary"
                        onClick={() =>
                          void run(
                            () => removeIntegration(sb, tenantId, nc.integration.id),
                            'Nextcloud was removed. Files are archived in Teslyn Cloud again.',
                          )
                        }
                      >
                        Confirm remove
                      </Button>
                    ) : (
                      <Button
                        variant="quiet"
                        onClick={() => setConfirmRemove(true)}
                        aria-label="Remove Nextcloud"
                      >
                        Remove
                      </Button>
                    ))}
                  {!nc && admin && (
                    <Button onClick={() => setEditing(true)} aria-label="Add Nextcloud">
                      Use Nextcloud
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      {nc && nc.archive.last_test_at && (
        <p className="caption muted">
          Last test {when(nc.archive.last_test_at)}: {nc.archive.last_test_ok ? 'passed' : 'failed'}.{' '}
          {nc.archive.last_test_message}
        </p>
      )}
      {nc && nc.archive.last_error && nc.archive.last_error_at && (
        <p className="caption muted">
          Last upload problem {when(nc.archive.last_error_at)}: {nc.archive.last_error}
        </p>
      )}
      {!nc && !admin && (
        <p className="caption muted">A tenant admin can archive to your organisation’s Nextcloud instead.</p>
      )}
      <ProcessorFolder
        sb={sb}
        tenantId={tenantId}
        admin={admin}
        nextcloudFolder={nc ? nc.archive.nextcloud_folder : null}
      />
      {editing && (
        <NextcloudForm
          archive={nc ? nc.archive : null}
          whereToFind={provider.whereToFind}
          onCancel={() => setEditing(false)}
          onSave={(v) =>
            void run(
              () =>
                saveNextcloud(sb, {
                  tenantId,
                  integrationId: nc ? nc.integration.id : null,
                  ...v,
                }),
              nc
                ? 'The Nextcloud settings were replaced. Press “Test connection” to check them.'
                : 'Nextcloud is set up: files are archived there from now on. Press “Test connection” to check it.',
            )
          }
        />
      )}
    </div>
  );
}

interface NextcloudValue {
  readonly url: string;
  readonly folder: string;
  readonly username: string;
  readonly appPassword: string;
}

function NextcloudForm({
  archive,
  whereToFind,
  onCancel,
  onSave,
}: {
  readonly archive: ArchiveRow | null;
  readonly whereToFind: string;
  readonly onCancel: () => void;
  readonly onSave: (v: NextcloudValue) => void;
}): ReactElement {
  const [url, setUrl] = useState(archive?.nextcloud_url ?? '');
  const [folder, setFolder] = useState(archive?.nextcloud_folder ?? '');
  const [username, setUsername] = useState(archive?.nextcloud_username ?? '');
  const [appPassword, setAppPassword] = useState('');
  const [problem, setProblem] = useState<{ readonly field: 'where' | 'login'; readonly text: string } | null>(
    null,
  );
  const provider = integrationProvider('nextcloud')!;
  return (
    <form
      className="panel stack"
      aria-label={archive ? 'Replace Nextcloud' : 'Add Nextcloud'}
      onSubmit={(e) => {
        e.preventDefault();
        const where = checkNextcloudSettings({ url, folder });
        if (!where.ok) {
          setProblem({ field: 'where', text: where.error });
          return;
        }
        const login = checkCredential(provider, { username, password: appPassword });
        if (!login.ok) {
          setProblem({ field: 'login', text: login.error });
          return;
        }
        onSave({ url: where.value.url, folder: where.value.folder, username: username.trim(), appPassword });
      }}
    >
      <h2 className="heading">{archive ? 'Replace the Nextcloud settings' : 'Archive to Nextcloud'}</h2>
      <p className="body muted">{whereToFind}</p>
      <TextField
        label="Server address"
        spellCheck={false}
        autoComplete="off"
        autoFocus
        hint="The address you open Nextcloud at, for example https://nextcloud.example.com."
        value={url}
        onChange={setUrl}
        error={problem?.field === 'where' && /address/i.test(problem.text) ? problem.text : undefined}
      />
      <TextField
        label="Folder"
        spellCheck={false}
        autoComplete="off"
        hint="The folder in that user’s files that Teslyn archives into, for example /Teslyn. It must exist."
        value={folder}
        onChange={setFolder}
        error={problem?.field === 'where' && !/address/i.test(problem.text) ? problem.text : undefined}
      />
      <TextField
        label="Username"
        spellCheck={false}
        autoComplete="off"
        value={username}
        onChange={setUsername}
      />
      <TextField
        label="App password"
        type="password"
        autoComplete="new-password"
        hint="Made in Nextcloud for Teslyn. Not your own password."
        value={appPassword}
        onChange={setAppPassword}
        error={problem?.field === 'login' ? problem.text : undefined}
      />
      <div className="row">
        <Button variant="primary" type="submit">
          {archive ? 'Replace' : 'Save'}
        </Button>
        <Button variant="quiet" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

/**
 * Where Teslyn Field's processors' zips go (R165, decision 925; docs/specs/cloud/deliverables.md §5.3): the
 * processors' folder of the tenant's Nextcloud with one folder per project made inside it, or Teslyn Cloud storage
 * (Deliverables) without Nextcloud. The archive switch above does not stop them. Tenant admins set the folder.
 */
function ProcessorFolder({
  sb,
  tenantId,
  admin,
  nextcloudFolder,
}: {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  readonly admin: boolean;
  /** The archive's Nextcloud folder, or null when the archive is Teslyn Cloud. */
  readonly nextcloudFolder: string | null;
}): ReactElement {
  const [set, setSet] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const reload = useCallback(() => {
    void loadProcessorFolder(sb, tenantId).then(setSet);
  }, [sb, tenantId]);
  useEffect(reload, [reload]);
  const where =
    nextcloudFolder === null
      ? 'Teslyn Cloud storage: staff download them from Deliverables.'
      : `Nextcloud: ${processorFolder(set, nextcloudFolder)}/<project folder>/`;

  async function save(): Promise<void> {
    const p = processorFolderProblem(value);
    setProblem(p);
    if (p) return;
    try {
      await setProcessorFolder(sb, tenantId, value);
      setEditing(false);
      setNotice(
        value.trim()
          ? `The processors’ zips go to ${value.trim()} from now on.`
          : 'The processors’ zips go to the default folder from now on.',
      );
      reload();
    } catch (e: unknown) {
      setProblem(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div className="stack" role="group" aria-label="Processors’ zips">
      <h3 className="label">Processors’ zips</h3>
      <p className="caption muted">
        After a daily report is published to the client, Teslyn Field zips each flight’s files for the data
        processors and emails them. They go to {where} Switching the archive off does not stop them.
      </p>
      {notice && (
        <p className="notice" role="status">
          {notice}
        </p>
      )}
      {admin && nextcloudFolder !== null && !editing && (
        <div className="row">
          <Button
            variant="quiet"
            onClick={() => {
              setValue(set ?? '');
              setEditing(true);
            }}
          >
            Change the processors’ folder
          </Button>
        </div>
      )}
      {editing && (
        <div className="row row--wrap">
          <TextField
            label="Processors’ folder (in the Nextcloud account’s files)"
            value={value}
            onChange={setValue}
            spellCheck={false}
            hint={`Such as /Shared/Field Data. Empty: ${nextcloudFolder ?? ''}/Field Data. The folder must exist; Teslyn makes each project's folder inside it.`}
            error={problem ?? undefined}
          />
          <Button variant="primary" onClick={() => void save()}>
            Save
          </Button>
          <Button variant="quiet" onClick={() => setEditing(false)}>
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
}
